import { useMemo } from 'react'

import { useTailgateGrid } from '../../hooks/useTailgateGrid'
import { useVideoOverlayDimensions } from '@/hooks/useVideoOverlayDimensions'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Flex as DarkFlex,
  NumberInput as DarkNumberInput,
  Text as DarkText,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { FormNumberInputControl } from '@/components/ui'
import { VideoPlayer, useIncidentVideo } from '@/features/video-player'
import { IncidentType } from '@/graphql/generated/schemas'

import { ScalableGridOverlay } from '../../components/ScalableGridOverlay'
import { IncidentOptionI } from '../../types/types'
import { getBoxInput } from '../../utils/utils'

interface IncidentVideoCountsPanelIProps {
  colorScheme?: ColorScheme
  isTailgateExperience: boolean
  selectedIncident: IncidentOptionI | null
  peopleIn: number
  peopleOut: number
  setPeopleIn: (n: number) => void
  setPeopleOut: (n: number) => void
}

export const IncidentVideoCountsPanel = ({
  colorScheme,
  isTailgateExperience,
  selectedIncident,
  peopleIn,
  peopleOut,
  setPeopleIn,
  setPeopleOut,
}: IncidentVideoCountsPanelIProps) => {
  const { t } = useTranslation()
  const tokenPathPrefix = colorScheme ? tokenPrefix(colorScheme) : ''
  const secondaryTextColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}text.secondary`)
    : 'gray.600'
  const panelBackground = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}bg.card`)
    : 'gray.50'
  const panelBorder = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}border.default`)
    : 'gray.200'
  const { inBox, outBox, videoDimensions } = useTailgateGrid()

  const { url: videoUrl, isProcessing: isVideoProcessing } = useIncidentVideo({
    incidentId: selectedIncident?.id ?? '',
    incidentType: selectedIncident?.type ?? IncidentType.Tailgating,
    hasVideoClip: true,
    skip: !selectedIncident?.id,
  })

  const {
    viewWidth,
    viewHeight,
    onVideoDimensions,
    cameraDimensions,
    clipDimensions,
  } = useVideoOverlayDimensions({
    configuredWidth: videoDimensions?.width,
    configuredHeight: videoDimensions?.height,
    sourceKey: videoUrl,
  })

  const tailgateGrid = useMemo(
    () => ({
      inBox: inBox?.isValid ? getBoxInput(inBox) : null,
      outBox: outBox?.isValid ? getBoxInput(outBox) : null,
    }),
    [inBox, outBox]
  )
  if (!selectedIncident) {
    if (colorScheme) {
      return (
        <DarkFlex
          alignItems='center'
          bg={panelBackground}
          borderRadius='12px'
          height='100%'
          justifyContent='center'
          minHeight='300px'
        >
          <DarkText color={secondaryTextColor} fontSize='14px'>
            Select an incident to preview video
          </DarkText>
        </DarkFlex>
      )
    }

    return (
      <Flex
        align='center'
        bg={panelBackground}
        borderRadius='xl'
        h='100%'
        justify='center'
        minH='300px'
      >
        <Text color={secondaryTextColor} fontSize='sm'>
          Select an incident to preview video
        </Text>
      </Flex>
    )
  }

  const video = (
    <>
      <VideoPlayer
        isProcessing={isVideoProcessing}
        onVideoDimensions={onVideoDimensions}
        showControls
        showToggleLiveButton={false}
        suppressGlobalPlayState
        url={videoUrl ?? ''}
      />
      <ScalableGridOverlay
        cameraDimensions={cameraDimensions}
        clipDimensions={clipDimensions}
        tailgateGrid={tailgateGrid}
        viewHeight={viewHeight}
        viewWidth={viewWidth}
      />
    </>
  )

  const legacyCountInputs = (
    <>
      <FormNumberInputControl
        defaultValue={0}
        id='peopleIn'
        label='Expected People IN'
        min={0}
        onChange={(_s, n) => setPeopleIn(n)}
        value={peopleIn}
        width='220px'
      />
      <FormNumberInputControl
        defaultValue={0}
        id='peopleOut'
        label='Expected People OUT'
        min={0}
        onChange={(_s, n) => setPeopleOut(n)}
        value={peopleOut}
        width='220px'
      />
    </>
  )

  if (colorScheme) {
    return (
      <>
        <DarkBox position='relative'>{video}</DarkBox>
        <DarkBox
          border='1px solid'
          borderColor={panelBorder}
          borderRadius='12px'
          flex='1'
          mt='16px'
          p='24px'
        >
          <DarkText fontSize='18px' fontWeight='bold'>
            {selectedIncident.incidentId}
          </DarkText>
          <DarkText color={secondaryTextColor} fontSize='14px' mt='4px'>
            {isTailgateExperience
              ? t('tailgate:grid_test_v2.badge_events_hint')
              : t('tailgate:incident_video_counts_panel.hint_text')}
          </DarkText>
          {!isTailgateExperience && (
            <DarkFlex gap='24px' justifyContent='center' mt='16px'>
              <DarkNumberInput
                colorScheme={colorScheme}
                id='peopleIn'
                label='Expected People IN'
                max={365}
                min={0}
                onValueChange={setPeopleIn}
                value={peopleIn}
                width='220px'
              />
              <DarkNumberInput
                colorScheme={colorScheme}
                id='peopleOut'
                label='Expected People OUT'
                max={365}
                min={0}
                onValueChange={setPeopleOut}
                value={peopleOut}
                width='220px'
              />
            </DarkFlex>
          )}
        </DarkBox>
      </>
    )
  }

  return (
    <>
      <Box position='relative'>{video}</Box>
      <Box
        border='1px solid'
        borderColor={panelBorder}
        borderRadius='xl'
        flex={1}
        mt={4}
        p={6}
      >
        <Text fontSize='lg' fontWeight='bold'>
          {selectedIncident.incidentId}
        </Text>
        <Text color={secondaryTextColor} fontSize='sm' mt={1}>
          {isTailgateExperience
            ? t('tailgate:grid_test_v2.badge_events_hint')
            : t('tailgate:incident_video_counts_panel.hint_text')}
        </Text>
        {!isTailgateExperience && (
          <Flex gap={6} justifyContent='center' mt={4}>
            {legacyCountInputs}
          </Flex>
        )}
      </Box>
    </>
  )
}
